import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router, NavigationEnd, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';

import { MenuItemCustom } from './header/header.component';
import { UserProfilePortalEndpoint } from '@api/services/user-profile-portal-endpoint';
import { CreditUnionPortalEndpoint } from '@api/services/credit-union-portal-endpoint';
import { UserProfile } from '@api/models/user-profile-portal.model';
import { CurrentCreditUnion } from '@api/models/credit-union-portal.model';
import { UserState } from '@core/state/user-state';
import { AuthService } from '@core/services/auth.service';
import { CreditUnionsService } from '@core/services/credit-unions.service';
import { LocalStorageService, STORAGE_KEYS } from '../shared/services/local-storage.service';
import { CustomDialogService } from '../shared/services/custom-dialog.service';
import { ToastService } from '../shared/services/toast.service';
import { ChangeCreditUnionDialogComponent } from '../shared/dialogs/change-credit-union-dialog/change-credit-union-dialog.component';
import { WelcomeTourDialogComponent } from '../shared/dialogs/welcome-tour-dialog/welcome-tour-dialog.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { DrawerModule } from 'primeng/drawer';
import { LendingPreferencesBannerComponent } from '../shared/components/lending-preferences-banner/lending-preferences-banner.component';
import { WebinarBannerComponent } from '../shared/components/webinar-banner/webinar-banner.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  standalone: true,
  host: { class: 'block overflow-x-clip' },
  imports: [
    RouterLink,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    DrawerModule,
    LendingPreferencesBannerComponent,
    WebinarBannerComponent,
  ]
})
export class LayoutComponent implements OnInit {
  private userProfileEndpoint = inject(UserProfilePortalEndpoint);
  private creditUnionEndpoint = inject(CreditUnionPortalEndpoint);
  private router = inject(Router);
  private userState = inject(UserState);
  private authService = inject(AuthService);
  private creditUnionsService = inject(CreditUnionsService);
  private storageService = inject(LocalStorageService);
  private customDialogService = inject(CustomDialogService);
  private toastService = inject(ToastService);

  menuConfig: MenuItemCustom[] = [
    { path: '/participation-opportunities', label: 'Participation opportunities' },
    { path: '/registered-interests', label: 'Registered interests' },
    { path: '/reports', label: 'Reports' },
    { path: '/funded', label: 'Funded deals', position: 'right' },
    { path: '/compliance', label: 'Compliance', position: 'right' },
  ];

  userProfileData = this.userState.userProfileData;
  creditUnionName = this.userState.creditUnionName;
  hasMultipleCreditUnions = this.userState.hasMultipleCreditUnions;
  visible = signal(false);
  isLendingPreferencesVisible = computed(() => {
    this.remindMeLaterTrigger();

    const updatedAt = this.userState.lendingPreferencesUpdatedAt();
    const role = this.userState.userRole();
    if (role !== 'CREDIT_UNION_ADMIN') return false;
    if (updatedAt === undefined) return false;

    const remindMeLaterDate = this.storageService.getRaw(STORAGE_KEYS.LENDING_PREFERENCE_REMIND_ME_LATER);
    if (remindMeLaterDate) {
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      const daysSinceReminder = Date.now() - new Date(remindMeLaterDate).getTime();
      if (daysSinceReminder < sevenDaysInMs) {
        return false;
      } else {
        this.storageService.remove(STORAGE_KEYS.LENDING_PREFERENCE_REMIND_ME_LATER);
      }
    }

    if (updatedAt === null) return true;

    const threeMonthsInMs = 90 * 24 * 60 * 60 * 1000;
    return (Date.now() - new Date(updatedAt).getTime()) > threeMonthsInMs;
  });

  isFirstTimeLendingPreferencesSetup = computed(() => {
    return this.userState.lendingPreferencesUpdatedAt() === null;
  });

  isTopBannerVisible = signal(false);

  showAllMessages = signal(false);
  currentUrl = signal(this.router.url);
  remindMeLaterTrigger = signal(0);

  routePath = computed(() => this.currentUrl().split('?')[0].split('#')[0].replace(/\/$/, ''));

  isHeaderSticky = computed(() => this.routePath() === '/participation-opportunities');

  isReportsPage = computed(() => this.routePath() === '/reports' || this.routePath().startsWith('/reports/'));

  isFullWidthPage = computed(() => this.isReportsPage() && this.routePath() !== '/reports/bulk-export');

  areBannersHidden = computed(() => this.isReportsPage());

  mainClasses = computed(() => {
    return this.isFullWidthPage()
      ? 'block w-full'
      : 'block max-w-[1400px] mx-auto w-full px-4 min-h-[calc(100vh-300px)]';
  });

  hasMultipleBanners = computed(() => {
    return !this.currentUrl().includes('lending-preferences') && this.isLendingPreferencesVisible() && this.isTopBannerVisible();
  });

  shouldStackBanners = computed(() => {
    return this.hasMultipleBanners() && !this.showAllMessages();
  });

  shouldShowAllMessagesLink = computed(() => {
    return this.hasMultipleBanners() && !this.showAllMessages();
  });

  ngOnInit() {
    this.getUserInfo(true);
    this.getCreditUnionInfo();
    // portal admins can access thousands of credit unions, so their list is loaded only when the switch dialog opens
    if (!this.userState.isPortalAdmin()) {
      this.creditUnionsService.loadCreditUnions().subscribe();
    }

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentUrl.set(event.url);
      this.showAllMessages.set(false);
    });
  }

  openWelcomeTour(profile: UserProfile) {
    if (profile.onboardingCompleted || this.storageService.get(STORAGE_KEYS.WELCOME_TOUR_SKIPPED, false)) return;

    this.customDialogService.openDialog(WelcomeTourDialogComponent, {
      width: '600px',
      showHeader: false,
      styleClass: 'welcome-tour-dialog',
      breakpoints: { '640px': 'calc(100vw - 32px)' }
    });
  }

  getUserInfo(withWelcomeTour = false) {
    this.userProfileEndpoint.getUserProfile().subscribe((res: UserProfile) => {
      this.userState.setProfile(res);
      if (withWelcomeTour) {
        this.openWelcomeTour(res);
      }
    });
  }

  getCreditUnionInfo() {
    this.creditUnionEndpoint.getCurrentCreditUnion().subscribe((res: CurrentCreditUnion) => {
      this.userState.setCreditUnion(res);
    });
  }

  openChangeCreditUnion() {
    this.hideDrawer();
    this.customDialogService.openDialog(ChangeCreditUnionDialogComponent, {
      header: 'Change credit union',
      styleClass: 'custom-dialog change-credit-union-dialog',
      width: '650px',
      breakpoints: { '576px': 'calc(100vw - 32px)' }
    });
    this.customDialogService.ref?.onClose.subscribe((changed) => {
      if (changed) {
        this.getUserInfo();
        this.getCreditUnionInfo();
        this.router.navigate(['/participation-opportunities']);
        this.toastService.success('Credit union changed successfully');
      }
    });
  }

  toggleShowAllMessages() {
    this.showAllMessages.set(true);
  }

  onRemindMeLater() {
    this.remindMeLaterTrigger.update(value => value + 1);
  }

  logout() {
    this.hideDrawer();
    this.authService.logout();
  }

  hideDrawer() {
    this.visible.set(false);
  }
}

